// src/components/user/Masivo/DeliveriesReview.jsx
import React, { useState } from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Search,
  Download,
  Trash2,
  RefreshCw,
  Calendar
} from 'lucide-react';
import DeliveryRowCard from './DeliveryRowCard';
import DeliveryEditModal from './DeliveryEditModal';
import { generateExcelFile } from '../../../utils/excelParser';

function DeliveriesReview({ 
  validatedDeliveries, 
  onUpdateDelivery, 
  onDeleteDelivery,
  onProceed,
  onChangeDate,
  selectedDate,
  validDistricts,
  userProducts
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, valid, warnings, errors
  const [editingDelivery, setEditingDelivery] = useState(null);

  // Calculate summary
  const totalCount = validatedDeliveries.length;
  const validCount = validatedDeliveries.filter(d => d.isValid).length;
  const errorCount = validatedDeliveries.filter(d => !d.isValid).length;
  const warningCount = validatedDeliveries.filter(d => d.hasWarnings && d.isValid).length;
  const canProceed = errorCount === 0 && totalCount > 0;

  // Filter deliveries
  const filteredDeliveries = validatedDeliveries.filter(delivery => {
    // Status filter
    if (filterStatus === 'valid' && (!delivery.isValid || delivery.hasWarnings)) return false;
    if (filterStatus === 'warnings' && !delivery.hasWarnings) return false;
    if (filterStatus === 'errors' && delivery.isValid) return false;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const data = delivery.data;
      return (
        data['Nombre y Apellido']?.toLowerCase().includes(search) ||
        data['WhatsApp']?.includes(search) ||
        data['Distrito']?.toLowerCase().includes(search) ||
        data['Producto']?.toLowerCase().includes(search) ||
        data['SKU']?.toLowerCase().includes(search) ||
        delivery.rowNumber.toString().includes(search)
      );
    }

    return true;
  });

  function handleDeleteDelivery(delivery) {
    if (window.confirm(`¿Eliminar la fila ${delivery.rowNumber}?`)) {
      onDeleteDelivery(delivery.rowNumber);
    }
  }

  function handleDownloadErrors() {
    const errorRows = validatedDeliveries
      .filter(d => !d.isValid)
      .map(d => ({
        Fila: d.rowNumber,
        ...d.data,
        Errores: d.errors.map(e => e.message).join('; ')
      }));
    
    const blob = generateExcelFile(errorRows, 'errores_carga_masiva.xlsx');
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'errores_carga_masiva.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  function formatDateForDisplay(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('es-PE', options);
  }

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-extrabold text-brand-primary font-montserrat mb-2">
          Paso 3: Revisa tus Entregas
        </h2>
        <p className="text-sm text-slate-600 font-montserrat">
          Valida y corrige los datos antes de crear las entregas
        </p>
      </div>

      {/* Selected date display */}
      <div className="bg-gradient-to-r from-brand-primary/10 to-brand-green/10 rounded-xl p-4 mb-6 border border-brand-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-brand-primary" />
            <div>
              <p className="text-xs text-slate-600 font-montserrat">Fecha seleccionada</p>
              <p className="text-sm font-bold text-brand-primary font-montserrat capitalize">
                {formatDateForDisplay(selectedDate)}
              </p>
            </div>
          </div>
          <button
            onClick={onChangeDate}
            className="text-sm text-brand-primary hover:underline font-montserrat flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Cambiar
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Total</p>
          <p className="text-2xl font-bold text-slate-900 font-montserrat">{totalCount}</p>
        </div>
        
        <div className="bg-brand-green/10 rounded-xl p-4 border border-brand-green/30">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Válidas</p>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-brand-green" />
            <p className="text-2xl font-bold text-brand-green font-montserrat">{validCount}</p>
          </div>
        </div>
        
        <div className="bg-[#E6B43C]/10 rounded-xl p-4 border border-[#E6B43C]/30">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Advertencias</p>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#E6B43C]" />
            <p className="text-2xl font-bold text-[#E6B43C] font-montserrat">{warningCount}</p>
          </div>
        </div>
        
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Errores</p>
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <p className="text-2xl font-bold text-red-600 font-montserrat">{errorCount}</p>
          </div>
        </div>
      </div>

      {/* Status message */}
      {!canProceed && errorCount > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-900 font-montserrat mb-1">
                Tienes {errorCount} entrega{errorCount !== 1 ? 's' : ''} con errores
              </p>
              <p className="text-sm text-red-700 font-montserrat">
                Corrígelas o elimínalas para continuar
              </p>
            </div>
          </div>
        </div>
      )}

      {canProceed && (
        <div className="bg-brand-green/10 border-l-4 border-brand-green rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
            <p className="text-sm font-bold text-brand-green font-montserrat">
              ¡Todo listo! Puedes crear {validCount} entrega{validCount !== 1 ? 's' : ''} ahora
            </p>
          </div>
        </div>
      )}

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, teléfono, distrito, fila..."
            className="w-full pl-10 pr-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm"
          />
        </div>

        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm"
        >
          <option value="all">Todas ({totalCount})</option>
          <option value="valid">Solo válidas ({validCount - warningCount})</option>
          <option value="warnings">Con advertencias ({warningCount})</option>
          <option value="errors">Con errores ({errorCount})</option>
        </select>
      </div>

      {/* Deliveries list */}
      <div className="space-y-3 mb-6 max-h-[500px] overflow-y-auto">
        {filteredDeliveries.length > 0 ? (
          filteredDeliveries.map(delivery => (
            <DeliveryRowCard
              key={delivery.rowNumber}
              delivery={delivery}
              onEdit={setEditingDelivery}
              onDelete={handleDeleteDelivery}
            />
          ))
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-500 font-montserrat">
              No se encontraron entregas con los filtros seleccionados
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {errorCount > 0 && (
          <button
            onClick={handleDownloadErrors}
            className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-300 text-red-700 rounded-xl font-semibold hover:bg-red-50 transition-colors font-montserrat"
          >
            <Download className="w-5 h-5" />
            <span>Descargar Errores</span>
          </button>
        )}

        <button
          onClick={onProceed}
          disabled={!canProceed}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-green text-white rounded-xl font-semibold hover:bg-brand-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
        >
          <CheckCircle className="w-5 h-5" />
          <span>Crear {validCount} Entrega{validCount !== 1 ? 's' : ''}</span>
        </button>
      </div>

      {/* Edit modal */}
      {editingDelivery && (
        <DeliveryEditModal
          delivery={editingDelivery}
          onSave={onUpdateDelivery}
          onClose={() => setEditingDelivery(null)}
          validDistricts={validDistricts}
          userProducts={userProducts}
        />
      )}
    </div>
  );
}

export default DeliveriesReview;